import React from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Badge,
  Image,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCcVisa,
  faCcMastercard,
  faCcDiscover,
  faCcAmex,
} from '@fortawesome/free-brands-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet';
import Imgix from 'react-imgix';

import {
  Loading,
  BitsyModal,
  FieldGroup,
  PreCheckoutPopup,
  SubComparisonTable,
} from '../../components';
import ProductReviewRating from '../../components/product-review-rating';
import SchemaData from './SchemaData';
import './style.css';
import Environment from '../../services/Environment';
import {
  Reviews,
  Colors,
  Variations,
  Thumbnails,
  ProductCarousel,
  TagsBadges,
} from '../../components/product';

// Services
import CartUtils from '../../services/CartUtils';
import Auth from '../../services/Auth';
import MetaTagService from '../../services/MetaTagService';
import MoneyUtils from '../../services/MoneyUtils';
import AddOnUtils from '../../services/AddOnUtils';
import BBAnalytics from '../../services/BBAnalytics';
import BitsyApollo from '../../services/BitsyApollo';
import Features from '../../services/Features';
import Logger from '../../services/Logger';
import Images from '../../images';

class Product extends React.Component {
  constructor() {
    super();
    this.state = {
      color: 'NONE',
      variation: 'NONE',
      quantity: 1,
      variationName: 'Variation',
      bigMediaImage:
        'https://s3.amazonaws.com/bitsy-product-images/default-product-image.png',
      defaultMediaImage:
        'https://s3.amazonaws.com/bitsy-product-images/default-product-image.png',
      selectedImageIndex: 0,
      modalType: 'NONE',
      modalHeader: '',
      quantityDictionary: {},
      hasReviewBeenSubmitted: false,
      renderColors: false,
      renderVariations: false,
      showAddToNextButton: true,
      review: {
        stars: 5,
        description: '',
      },
      reviewValidationState: {
        stars: true,
        description: false,
      },
      quantityValidationState: true,
      colorValidationState: true,
      variationValidationState: true,
      hasItemBeenAddedToCart: false,
      saleFetchComplete: false,
      activeSaleBadgeText: null,
      productIdsForSale: null,
    };
  }

  async fetchProduct() {
    this.setState({ isLoading: true });
    try {
      const productInfo = await BitsyApollo.getProduct({
        productUrlName: this.props.match.params.productUrlName,
      });

      if (productInfo?.quantityDictionaryAsString) {
        const newState = { productInfo };
        this.sendAnalyticsViewEvent(productInfo);
        const dictionary = JSON.parse(productInfo.quantityDictionaryAsString);
        newState.dictionary = dictionary;
        if (productInfo.media?.length) {
          newState.bigMediaImage = productInfo.media[0].url;
        }
        if (
          dictionary &&
          dictionary.colors &&
          dictionary.colors.length &&
          dictionary.colors[0] !== 'NONE'
        ) {
          newState.colorValidationState = false;
          newState.renderColors = true;
        }
        if (
          dictionary.variations &&
          dictionary.variations.length &&
          dictionary.variations[0] !== 'NONE'
        ) {
          newState.variationValidationState = false;
          newState.renderVariations = true;
        }
        this.setState(newState);
      }
    } catch (error) {
      Logger.error(error);
      this.setState({ error });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    await this.fetchProduct();

    await this.fetchAndLoadActiveSaleInfo();

    if (Auth.isLoggedIn()) {
      const res = await BitsyApollo.userInfoQuery();
      if (res && res.data) {
        const { userInfo } = res.data;
        if (
          userInfo &&
          userInfo.subscriptions &&
          userInfo.subscriptions.length
        ) {
          const activeSubscriptions = userInfo.subscriptions.filter(
            (sub) => sub.status === 'ACTIVE',
          );
          if (activeSubscriptions.length === 1) {
            this.setState({
              subscription: activeSubscriptions[0],
            });
          }
          if (activeSubscriptions.length > 1) {
            this.setState({
              showAddToNextButton: false,
              subscription: activeSubscriptions[0],
            });
          }
        }
      }
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.match.params.productUrlName !==
      prevProps.match.params.productUrlName
    ) {
      await this.fetchProduct();
    }
  }

  sendAnalyticsViewEvent(productInfo) {
    if (!this.state.analyticsSent) {
      if (productInfo.subscriptionPlan) {
        BBAnalytics.viewedSubscriptionEvent(
          productInfo.subscriptionPlan,
          productInfo,
        );
      } else {
        BBAnalytics.viewedProductEvent(
          productInfo,
          productInfo,
          this.props.location?.state?.isPromoForViewEvent,
          this.props.location?.state?.positionForViewEvent,
        );
      }
      this.setState({ analyticsSent: true });
    }
  }

  getCurrentQuantity() {
    if (this.state.productInfo.isDropShip || this.state.productInfo.isComposite)
      return 999;
    const dictionary = JSON.parse(
      this.state.productInfo.quantityDictionaryAsString,
    );
    if (this.state.variation !== 'NONE' && dictionary.colors[0] === 'NONE') {
      return dictionary[this.state.variation].overallQuantity;
    }
    if (this.state.color !== 'NONE' && dictionary.variations[0] === 'NONE') {
      return dictionary[this.state.color].overallQuantity;
    }
    if (this.state.variation !== 'NONE' && this.state.color !== 'NONE') {
      return dictionary[this.state.color][this.state.variation];
    }
    return this.state.productInfo.totalQuantity;
  }

  selectColor = (color, media) => {
    const newState = {
      color,
      colorValidationState: true,
      colorError: null,
    };

    const firstColorMatch = (mediaItem) => mediaItem.color === color;
    const firstColorMatchIndex = media.findIndex(firstColorMatch);

    if (firstColorMatchIndex !== -1) {
      newState.selectedImageIndex = firstColorMatchIndex;
    }

    this.setState(newState);
  };

  deselectColor = () => {
    this.setState({ color: 'NONE', colorValidationState: false });
  };

  toggleVariation = (selectedVariation, media) => {
    if (selectedVariation === this.state.variation) {
      this.setState({ variation: 'NONE', variationValidationState: false });
    } else {
      const newState = {
        variation: selectedVariation,
        variationValidationState: true,
        variationError: null,
      };

      const firstVariationMatch = (mediaItem) =>
        mediaItem.variation === selectedVariation;
      const firstVariationMatchIndex = media.findIndex(firstVariationMatch);
      if (firstVariationMatchIndex !== -1) {
        newState.selectedImageIndex = firstVariationMatchIndex;
      }

      this.setState(newState);
    }
  };

  fetchAndLoadActiveSaleInfo = async () => {
    // must have active sale, only one - it will return first, and must have included products

    try {
      const activeSaleData = await BitsyApollo.getActiveSaleEvents();
      if (
        activeSaleData &&
        activeSaleData[0] &&
        activeSaleData[0].includedProducts
      ) {
        this.setState({
          activeSaleBadgeText: activeSaleData[0].siteLabelText,
          productIdsForSale: activeSaleData[0].includedProducts.map(
            (product) => product._id,
          ),
          saleFetchComplete: true,
        });
      }
    } catch (error) {
      this.setState({ saleFetchComplete: true });
    }
  };

  handleQuantityChange = (event) => {
    this.setState({ quantity: event.target.value });
  };

  // added fillerArg in order to match the amount of arguments
  // in the Shop component. This is important because of the
  // preCheckout Popup component calling the handleAddToCart function
  handleAddToCart = async (productInfo, fillerArg, promoItem, list) => {
    const {
      colorValidationState,
      hasItemBeenAddedToCart,
      variationValidationState,
      quantityValidationState,
      quantity,
      color,
      variation,
      variationName,
    } = this.state;
    const newState = { ...this.state };
    const dictionary = JSON.parse(productInfo.quantityDictionaryAsString);
    newState.unknownError = null;
    if (!hasItemBeenAddedToCart) {
      this.setState({
        hasItemBeenAddedToCart: true,
        subscriptionPlan: productInfo.subscriptionPlan,
      });
    }

    if (!colorValidationState) {
      this.setState({ colorError: 'Oops! You must select a color' });
    }

    if (!variationValidationState) {
      this.setState({
        variationError: `Oops! You must select a ${
          productInfo.variationName || variationName
        }`,
      });
    }

    if (
      !quantityValidationState ||
      !colorValidationState ||
      !variationValidationState
    ) {
      return false;
    }

    let userQuantity = parseInt(quantity, 10);

    if (userQuantity < 1) {
      userQuantity = 1;
      newState.quantity = 1;
    } else {
      newState.quantity = userQuantity;
    }

    if (
      (dictionary[color][variation] < userQuantity ||
        productInfo.totalQuantity < userQuantity) &&
      productInfo.isDropShip === false &&
      productInfo.isComposite === false
    ) {
      newState.quantity = dictionary[color][variation];
      newState.modalType = 'ABOVE STOCK';
    }
    const shouldShowPreCheckoutPromo =
      this.state.modalType !== 'PRE-CHECKOUT PROMO';

    this.setState({ isLoading: true });
    const res = await CartUtils.addProductToCart(
      productInfo,
      variation,
      newState,
      Environment.store,
      promoItem,
      null,
      null,
      null,
      shouldShowPreCheckoutPromo,
      list,
    );

    if (res) {
      // Feature gate check
      const isFeatureGateOn =
        Features.featureIsEnabled('2024-enable_new2024checkout') &&
        Environment.store !== 'bitsy';
      if (!Auth.hasToken()) {
        await BitsyApollo.getEmptyUser();
      }
      const initCartInfo = await CartUtils.getCart();
      const hasSubscription = !!initCartInfo.subscription;
      const noProductsInCart = !initCartInfo.productsAndDetails?.length;
      const onlyGWPProductsInCart = initCartInfo.productsAndDetails?.every(
        (product) => product.isAddedCouponProduct,
      );
      const noInvalidProductsInCart = noProductsInCart || onlyGWPProductsInCart;
      if (hasSubscription && isFeatureGateOn && noInvalidProductsInCart) {
        this.props.history.push({
          pathname: '/checkout/start-membership',
          state: { initCartInfo },
        });
      } else {
        res.isLoading = false;
        this.setState(res);
      }
    }

    if (!shouldShowPreCheckoutPromo && this.state.subscriptionPlan) {
      this.goToCart();
    }

    return null;
  };

  handleAddToNextBox = async (product, addOnType) => {
    if (this.state.subscription) {
      this.handleAddOrRemoveAddOn(product, addOnType);
    } else {
      this.setState({ modalType: 'BECOME_SUBSCRIBER' });
    }
  };

  handleAddOrRemoveAddOn = async (product, addOnType) => {
    const newState = await AddOnUtils.handleAddOrRemoveAddOn(
      product,
      this.state,
      addOnType === 'multi',
    );

    await this.fetchProduct();
    this.setState(newState);
  };

  handleSubscriberExclusive = (productInfo, isSubscriberExclusive) => {
    const { history } = this.props;
    if (!this.state.subscription && isSubscriberExclusive) {
      history.push('/subscribe/select');
    } else {
      this.handleAddToCart(productInfo, null, null, 'Product');
    }
  };

  continueShopping = () => {
    const { history } = this.props;
    this.setState({ modalType: 'NONE' });
    this.props.onCartUpdate();
    history.push('/shop/all');
  };

  goToCart = () => {
    this.props.onCartUpdate();
    const { history } = this.props;
    if (Auth.isLoggedIn()) {
      history.push('/checkout/review');
    } else {
      history.push('/guest/checkout/review');
    }
  };

  handleThumbnailClick = (bigMediaImage, index) => {
    this.setState({
      bigMediaImage,
      selectedImageIndex: index,
    });
  };

  onCarouselChange = (index) => {
    this.setState({
      selectedImageIndex: index,
    });
  };

  handlePreCheckoutPopupExit = () => {
    if (this.state.subscriptionPlan) {
      this.goToCart();
    } else {
      this.setState({ modalType: 'CART SUCCESS' });
    }
  };

  scrollToComparisonTable = (e) => {
    e.preventDefault();
    const subComparisonRow = document.getElementById('subComparisonTable');
    if (subComparisonRow) {
      subComparisonRow.scrollIntoView({ behavior: 'smooth' });
    }
  };

  updateTitle = (title) => {
    if (title) {
      this.setState({ modalHeader: title });
    }
  };

  renderModal = () => {
    const { modalType, quantity } = this.state;
    if (modalType === 'THANK YOU') {
      return (
        <BitsyModal
          className='addOnToNextBoxThankYou'
          isShowingModal
          content='addOnToNextBoxThankYou'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithButton'
          onClickMainButton={() => this.setState({ modalType: 'NONE' })}
          data={{}}
        />
      );
    }
    if (modalType === 'REVIEW SUCCESS') {
      return (
        <BitsyModal
          isShowingModal
          content='reviewSuccess'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModal'
        />
      );
    }
    if (modalType === 'CART SUCCESS') {
      return (
        <BitsyModal
          isShowingModal
          content='cartSuccess'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithTwoButtons'
          onClickMainButton={() => this.goToCart()}
          onClickSecondaryButton={() => this.continueShopping()}
          data={{}}
        />
      );
    }
    if (modalType === 'ABOVE STOCK') {
      return (
        <BitsyModal
          isShowingModal
          content='aboveStock'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithButton'
          onClickMainButton={() => this.goToCart()}
          data={{
            additionalBody: `This item has been added to your cart but your specified quantity is more that what we have in stock. ${quantity} units have been added to your cart instead.`,
          }}
        />
      );
    }
    if (this.state.modalType === 'PRODUCT ERROR') {
      return (
        <BitsyModal
          isShowingModal
          content='addOnProductError'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithButton'
          onClickMainButton={() => this.setState({ modalType: 'NONE' })}
          data={{}}
        />
      );
    }
    if (this.state.modalType === 'PRE-CHECKOUT PROMO') {
      return (
        <BitsyModal
          isShowingModal
          id='preCheckoutPromoModal'
          hideExit
          onExit={this.handlePreCheckoutPopupExit}
          type='modalWithComponent'
          content={
            this.state.productInfo.subscriptionPlan
              ? 'preCheckoutPromoPopUpPremiumUpgrade'
              : 'preCheckoutPromoPopUp'
          }
          modalTitle={this.state.modalHeader}
          modalComponent={
            <PreCheckoutPopup
              updateTitle={this.updateTitle}
              addToCart={this.handleAddToCart}
              exitModal={this.handlePreCheckoutPopupExit}
              productAddedToCart={this.state.productAddedToCart}
              isRecurringAddOnPromo={this.state.isRecurringAddOnPromo}
            />
          }
        />
      );
    }
    if (this.state.modalType === 'BECOME_SUBSCRIBER') {
      return (
        <BitsyModal
          className='becomeASubscriberModal'
          isShowingModal
          content='becomeASubscriber'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithButton'
          onClickMainButton={() => this.props.history.push('/subscribe/select')}
          data={{
            helperText: (
              <div>
                <p>Already a Subscriber?</p>
                <p>
                  Sign in&nbsp;
                  <Link to='/signin'>here</Link>
                  &nbsp;to get your discount & add to your next box
                </p>
              </div>
            ),
          }}
        />
      );
    }
    if (this.state.modalType === 'THANK YOU RECURRING') {
      return (
        <BitsyModal
          isShowingModal
          content='recurringAddOnThankYou'
          onExit={() => this.setState({ modalType: 'NONE' })}
          type='bodyAndExitModalWithButton'
          onClickMainButton={() => this.setState({ modalType: 'NONE' })}
          data={{}}
        />
      );
    }
    return null;
  };

  checkAndAddRegisteredMark = (text) => {
    // const brandWithTrademark = text.includes('®') ? text : `${text} ®`;
    return text;
  };

  renderProductDescription = () => {
    if (this.state.productInfo.descriptionComponents?.length) {
      return this.state.productInfo.descriptionComponents.map(
        (descriptionComponent) => (
          <p key={`descriptionComponent-${descriptionComponent}`}>
            {descriptionComponent}
          </p>
        ),
      );
    }
    return (
      // eslint-disable-next-line react/no-danger
      <div
        dangerouslySetInnerHTML={{ __html: this.state.productInfo.description }}
      />
    );
  };

  renderFreeReturns = () => {
    if (
      this.state.productInfo &&
      this.state.productInfo.freeReturns === false
    ) {
      return null;
    }
    return (
      <Link
        to={{ pathname: 'https://support.bumpboxes.com', activeKey: 'fifth' }}
        target='_blank'
        title='Click here to see our FAQ'
        className='freeReturns'
      >
        <h4 className='freeReturns'>FREE RETURNS*</h4>
      </Link>
    );
  };

  renderLoading = () => {
    if (this.state.isLoading) {
      return <Loading />;
    }

    return null;
  };

  render() {
    const {
      bigMediaImage,
      renderColors,
      colorError,
      renderVariations,
      variationError,
      quantity,
      quantityError,
      variation,
      color,
      productInfo,
      error,
    } = this.state;

    let isHighlightSaleItem = false;

    if (this.state.isLoading) {
      return (
        <Container className='productPageContainer'>
          <Loading />
        </Container>
      );
    }

    if (!this.state.saleFetchComplete) {
      return (
        <Container className='productPageContainer'>
          <Loading />
        </Container>
      );
    }

    if (error) {
      this.props.history.push('/error');
      return <Loading />;
    }
    if (productInfo?._id === '404') {
      this.props.history.push('/Page404');
      return <Loading />;
    }
    if (productInfo.subscriptionPlan) {
      productInfo.averageRating = 5;
    }

    if (this.state.productIdsForSale.includes(productInfo._id)) {
      isHighlightSaleItem = true;
    }

    let buttonText = 'Add To Cart';
    let isButtonDisabled = false;
    let isAddToNextBoxButtonDisabled = false;
    let isSubscriberExclusive = false;

    if (productInfo.isOutOfStock) {
      buttonText = 'Out of Stock';
      isButtonDisabled = true;
      isAddToNextBoxButtonDisabled = true;
    }

    if (productInfo.tags && productInfo.tags.length) {
      if (
        productInfo.tags.some(
          (tag) => tag.description === 'Subscriber Exclusive',
        )
      ) {
        buttonText = 'Subscriber Exclusive';
        isSubscriberExclusive = true;
      }
    }

    let addToNextBoxButtonText = 'Add to Next Box';
    let addToAllBoxesButtonText = 'Add to All Boxes';
    let showReplacementItemText = false;
    if (this.state.subscription) {
      const productAlreadyAddedToAddOns = this.state.subscription.addOnsWithDetails.find(
        (addOn) => addOn.product._id === productInfo._id,
      );
      const productAlreadyAddedToRecurringAddOns = this.state.subscription.recurringAddOns.find(
        (addOn) => addOn.product._id === productInfo._id,
      );
      if (productAlreadyAddedToRecurringAddOns) {
        addToAllBoxesButtonText = 'Remove from All Boxes';
        isAddToNextBoxButtonDisabled = true;
      }
      if (productAlreadyAddedToAddOns) {
        if (!productAlreadyAddedToAddOns.replacementItem) {
          addToNextBoxButtonText = 'Remove from Next Box';
          isAddToNextBoxButtonDisabled = false;
        } else {
          showReplacementItemText = true;
        }
      }
    }

    const productQuantity = this.getCurrentQuantity();

    const metaDescription = productInfo.metaDescription
      ? productInfo.metaDescription
      : '';

    const metaTitle = productInfo.brand
      ? `${productInfo.brand} ${productInfo.name}`
      : productInfo.name;

    const isMobileSub = productInfo.subscriptionPlan && window.innerWidth < 768;

    return (
      <Container className='productPageContainer'>
        <Helmet>
          <title>{metaTitle}</title>
          <meta
            property='og:title'
            content={MetaTagService.getTemplatedTitle(metaTitle)}
          />
          <meta
            name='twitter:title'
            content={MetaTagService.getTemplatedTitle(metaTitle)}
          />
          <meta name='description' content={metaDescription} />
          <meta property='og:description' content={metaDescription} />
          <meta property='og:image' content={bigMediaImage} />
          <meta property='og:type' content='article' />
          <meta
            property='og:price:amount'
            content={productInfo.actualCost.toFixed(2)}
          />
          <meta property='og:price:currency' content='USD' />

          <meta
            property='product:retailer_item_id'
            content={MetaTagService.getProductSku(productInfo)}
          />
          <meta property='product:item_group_id' content={productInfo.brand} />

          <meta name='twitter:card' content='product' />
          <meta name='twitter:site' content='@bumpboxes' />
          <meta name='twitter:description' content={metaDescription} />
          <meta name='twitter:image' content={bigMediaImage} />
          <meta
            name='twitter:data1'
            content={productInfo.actualCost.toFixed(2)}
          />
          <meta name='twitter:label1' content='Price' />
          <meta itemProp='name' content={metaTitle} />
        </Helmet>

        {this.renderModal()}
        {this.renderLoading()}
        <Row className='productInfo'>
          {productInfo.subscriptionPlan && (
            <Col
              xs={12}
              sm={12}
              md={{ span: 6, offset: 6 }}
              lg={{ span: 6, offset: 6 }}
              className='mb-2 stepTitle'
            >
              <p>
                Step 1 of 3: <br /> Choose Your Subscription
              </p>
            </Col>
          )}
          <Col
            xs={12}
            sm={12}
            md={{ span: 6, offset: 6 }}
            lg={{ span: 6, offset: 6 }}
            className='productNameRow'
          >
            <Row>
              <Col>
                <h5 className='category-tags'>
                  <TagsBadges tags={productInfo.tags} />
                </h5>
              </Col>
            </Row>
            <Row>
              <Col>
                <h1>{productInfo.name}</h1>
              </Col>
            </Row>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <div className='productImage'>
              {productInfo.media.length > 1 ? (
                <ProductCarousel
                  media={productInfo.media}
                  activeIndex={this.state.selectedImageIndex}
                  onSelect={this.onCarouselChange}
                  mediaName={productInfo.name}
                />
              ) : (
                <Imgix
                  src={bigMediaImage
                    .replace(
                      /https:\/\/s3.amazonaws.com\/bitsy-product-images\//i,
                      'https://bump-boxes.imgix.net/',
                    )
                    .replace(
                      /https:\/\/bitsy-product-images.s3.amazonaws.com\//i,
                      'https://bump-boxes.imgix.net/',
                    )}
                  width={550}
                  height={550}
                  sizes='(max-width: 768px) 350px, 550px'
                  imgixParams={{ q: 75, auto: ['format', 'compress'] }}
                  htmlAttributes={{
                    alt: `${productInfo.name} Photo`,
                    title: productInfo.name,
                  }}
                />
              )}
            </div>
            {!isMobileSub && (
              <Row className='thumbnails'>
                <Thumbnails
                  onClick={this.handleThumbnailClick}
                  media={productInfo.media}
                  productName={productInfo.name}
                />
              </Row>
            )}
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Row className='productBrandRow'>
              <Col>
                <h3 id='sub-description' className='product-brand'>
                  {this.checkAndAddRegisteredMark(productInfo.brand)}
                </h3>
              </Col>
            </Row>
            <Row className='productReviewRow'>
              <ProductReviewRating product={productInfo} />
            </Row>

            {!productInfo.subscriptionPlan ||
            productInfo.subscriptionPlan.months === '3' ? (
              <Row>
                <Col xs={12} sm={12} md={12} lg={12}>
                  {isHighlightSaleItem && this.state.activeSaleBadgeText ? (
                    <Badge
                      id='b-saleBadge'
                      className='bump-saleBadge'
                      variant='primary'
                    >
                      {this.state.activeSaleBadgeText}
                    </Badge>
                  ) : null}
                </Col>

                <Col xs={12} sm={12} md={12} lg={12}>
                  {productInfo.discounted && (
                    <div>
                      <span className='productPageRetailPriceLabel'>
                        Retail Price
                      </span>
                      $
                      <span className='retailValueText'>
                        {MoneyUtils.formatAsMoney(productInfo.originalCost)}
                      </span>
                    </div>
                  )}
                  <div className='subBoxSubHeader'>
                    <div className='inline-text firstBoxDiscount'>
                      {productInfo.discountDisplayText ? (
                        <span className='productPageDiscountCallout'>
                          {productInfo.discountDisplayText}
                        </span>
                      ) : null}
                      ${MoneyUtils.formatAsMoney(productInfo.actualCost)}{' '}
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col xs={12} sm={12} md={12} lg={12}>
                  {isHighlightSaleItem && this.state.activeSaleBadgeText ? (
                    <Badge
                      id='b-saleBadge'
                      className='bump-saleBadge'
                      variant='primary'
                    >
                      {this.state.activeSaleBadgeText}
                    </Badge>
                  ) : null}
                </Col>
                <Col xs={12} sm={12} md={12} lg={12}>
                  {productInfo.subscriptionPlan.months === 'INFINITY' ? (
                    <div className='subBoxSubHeader'>
                      <div className='inline-text firstBoxDiscount'>
                        {productInfo.discountDisplayText ? (
                          <span className='productPageDiscountCallout'>
                            {productInfo.discountDisplayText}
                          </span>
                        ) : null}
                        $
                        {MoneyUtils.formatAsMoney(
                          productInfo.subscriptionPlan.costWithCoupon,
                        )}{' '}
                        <span className='firstMonthText'>First Month</span>
                      </div>
                    </div>
                  ) : (
                    <div className='subBoxSubHeader'>
                      <div className='inline-text firstBoxDiscount'>
                        {productInfo.discountDisplayText ? (
                          <span className='productPageDiscountCallout'>
                            {productInfo.discountDisplayText}
                          </span>
                        ) : null}
                        $
                        {MoneyUtils.formatAsMoney(
                          productInfo.subscriptionPlan.costWithCoupon,
                        )}{' '}
                      </div>
                    </div>
                  )}
                  <div>
                    {/* eslint no-nested-ternary: "warn" */}
                    <div className='inline-text monthlyRenewal'>
                      {productInfo.subscriptionPlan.months === 'INFINITY'
                        ? `$${MoneyUtils.formatAsMoney(
                            productInfo.subscriptionPlan.actualCost,
                          )} monthly renewal`
                        : MoneyUtils.formatAsMoney(
                            productInfo.subscriptionPlan.costWithCoupon,
                          )}
                    </div>
                  </div>
                  {productInfo.subscriptionPlan.months === 'INFINITY' ? (
                    <Row>
                      <Col
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        className='subLinks-container'
                      >
                        <Link
                          to={`/products/${
                            productInfo.subscriptionPlan.productUrlPrefix || ''
                          }six-month-pregnancy-subscription-box`}
                          className={
                            productInfo.subscriptionPlan.minimumMonths === 6
                              ? 'subSelected firstSubLink'
                              : 'firstSubLink'
                          }
                        >
                          6 month subscription
                        </Link>
                        <Link
                          to={`/products/${
                            productInfo.subscriptionPlan.productUrlPrefix || ''
                          }nine-month-pregnancy-subscription-box`}
                          className={
                            productInfo.subscriptionPlan.minimumMonths === 9
                              ? 'subSelected'
                              : ''
                          }
                        >
                          9 month subscription
                        </Link>
                        <Link
                          to={`/products/${
                            productInfo.subscriptionPlan.productUrlPrefix || ''
                          }twelve-month-pregnancy-subscription-box`}
                          className={
                            productInfo.subscriptionPlan.minimumMonths === 12
                              ? 'subSelected'
                              : ''
                          }
                        >
                          12 month subscription
                        </Link>
                        <Link
                          to={`/products/${
                            productInfo.subscriptionPlan.productUrlPrefix || ''
                          }month-to-month-bump`}
                          className={
                            productInfo.subscriptionPlan.minimumMonths === null
                              ? 'subSelected'
                              : ''
                          }
                        >
                          monthly subscription
                        </Link>
                      </Col>
                      <Col
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        className='subComparisonButton-container'
                      >
                        <div>
                          <a
                            href='#subComparisonTable' /* Optionally set this if the section has an ID */
                            onClick={
                              this.scrollToComparisonTable
                            } /* Keep the onClick handler */
                            className='mt-2 subComparisonButton' /* Keep the styling class */
                          >
                            View Comparison of Subscription Plans
                          </a>
                        </div>
                      </Col>
                    </Row>
                  ) : null}
                </Col>
              </Row>
            )}
            {productQuantity < 10 &&
            productQuantity > 0 &&
            productInfo.totalQuantity > 0 &&
            !productInfo.isDropShip &&
            !productInfo.isComposite ? (
              <Row className='fewLeft'>
                <Col md={8} lg={8}>
                  Only {productQuantity} left in stock!
                </Col>
              </Row>
            ) : null}
            {Environment.store.includes('bump') &&
            !productInfo.subscriptionPlan ? (
              <Row>
                <Col lg={6}>
                  <p className='free-shipping'>
                    <span>Free Shipping</span> on Orders $49+
                  </p>
                </Col>
              </Row>
            ) : null}
            {renderColors && (
              <div>
                <Row>
                  <Col lg={6}>
                    <p className='align-right'>Select Color:</p>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    {colorError && (
                      <div className='form-error'>{colorError}</div>
                    )}
                  </Col>
                </Row>
                <Row className='product-colors-wrap'>
                  <Colors
                    productInfo={productInfo}
                    variation={variation}
                    color={color}
                    selectColor={this.selectColor}
                    deselectColor={this.deselectColor}
                  />
                </Row>
              </div>
            )}
            {renderVariations && (
              <div>
                <Row>
                  <Col lg={6}>
                    {variationError && (
                      <div className='form-error'>{variationError}</div>
                    )}
                  </Col>
                </Row>
                <Variations
                  productInfo={productInfo}
                  color={color}
                  selectedVariation={variation}
                  toggleVariation={this.toggleVariation}
                />
              </div>
            )}
            {!productInfo.subscriptionPlan ? (
              <>
                <Row>
                  <Col lg={6}>
                    <Form className='quantityInput'>
                      <FieldGroup
                        controlId='quantity'
                        type='number'
                        name='quantity'
                        label='Quantity'
                        className='quantityInputControl'
                        value={quantity}
                        handleChange={this.handleQuantityChange}
                        min={1}
                      />
                    </Form>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    {quantityError && (
                      <div className='form-error'>{quantityError}</div>
                    )}
                  </Col>
                </Row>
              </>
            ) : null}
            <Row className='addToCartButtonRow'>
              <Col className='addToCartButtonCol'>
                <Button
                  disabled={isButtonDisabled}
                  variant='primary'
                  className='addToCartButton'
                  onClick={() =>
                    this.handleSubscriberExclusive(
                      productInfo,
                      isSubscriberExclusive,
                    )
                  }
                >
                  {buttonText}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    size='lg'
                    className='ml-2'
                  />
                </Button>
              </Col>
            </Row>
            {productInfo.shouldShowAddToNextBoxForProduct &&
            this.state.showAddToNextButton &&
            !showReplacementItemText ? (
              <Row className='addToNextBoxRow'>
                <Col className='addToNextBoxColum'>
                  <Button
                    disabled={isAddToNextBoxButtonDisabled}
                    variant='default'
                    className='addToNextBoxButton'
                    onClick={() =>
                      this.handleAddToNextBox(productInfo, 'single')
                    }
                  >
                    {addToNextBoxButtonText}
                  </Button>
                </Col>
              </Row>
            ) : null}
            {productInfo.shouldShowAddToNextBoxForProduct &&
            this.state.showAddToNextButton ? (
              <Row className='addToNextBoxRow'>
                <Col className='addToNextBoxColum'>
                  <Button
                    variant='default'
                    className='addToNextBoxButton'
                    onClick={() =>
                      this.handleAddToNextBox(productInfo, 'multi')
                    }
                  >
                    {addToAllBoxesButtonText}
                  </Button>
                </Col>
              </Row>
            ) : null}
            {showReplacementItemText ? (
              <Row className='replacementItemText'>
                <Col>
                  This item is being sent in your next subscription delivery as
                  a replacement (at no cost to you)!
                </Col>
              </Row>
            ) : null}
            {productInfo.actualCost > 0 ? (
              <Row>
                <Col xs={12} sm={12} md={12} lg={12}>
                  Purchase this product now and earn{' '}
                  <strong>{Math.round(productInfo.actualCost)}</strong> points!
                </Col>
              </Row>
            ) : null}
            <Row className='product-creditcard'>
              <Col xs={10} sm={6} md={6} lg={6}>
                <FontAwesomeIcon icon={faCcVisa} />
                <FontAwesomeIcon icon={faCcMastercard} />
                <FontAwesomeIcon icon={faCcDiscover} />
                <FontAwesomeIcon icon={faCcAmex} />
              </Col>
            </Row>

            <Row className='freeReturns-container'>
              <Col xs={12} sm={12} md={12} lg={6}>
                {this.renderFreeReturns()}
              </Col>
            </Row>

            <Row className='product-description mt-3'>
              <Col xs={12} sm={12} md={12} lg={12}>
                {this.renderProductDescription()}
              </Col>
              <Col lg={12}>
                {productInfo.ingredients ? (
                  <div className='my-3'>
                    <h4>Ingredients:</h4>
                    {productInfo.ingredients}
                  </div>
                ) : null}
              </Col>
            </Row>

            {Environment.store !== 'bitsy' &&
              productInfo.tags?.filter((x) => x.description === 'Gift Boxes')
                .length > 0 &&
              Features.checkGate('2024-consumer-usesubscriptionblockonpdp') && (
                <Row>
                  <Col fluid>
                    <Row className='pdpSubscriptionBlock'>
                      <Col xs={6} sm={6} md={6} lg={8} xl={8}>
                        <Row>
                          <Col xs={0} sm={0} md={0} lg={0} xl={1} />

                          <Col className='pdpSubscriptionBlock-txt'>
                            <p className='pdpSubscriptionBlock-header'>
                              #1 Pregnancy Box
                            </p>
                            Save tons of $$$ on pregnancy essentials delivery to
                            your door every month.
                            <Row>
                              <Col xs={10} sm={10} md={10} lg={7} xl={7}>
                                <Button
                                  className='pdpSubscriptionBlock-cta-button'
                                  href='/products/six-month-pregnancy-subscription-box'
                                >
                                  START TODAY
                                </Button>{' '}
                              </Col>
                            </Row>
                          </Col>

                          <Col xs={0} sm={0} md={0} lg={0} xl={1} />
                        </Row>
                      </Col>
                      <Col
                        xs={6}
                        sm={6}
                        md={6}
                        lg={4}
                        xl={4}
                        className='pdpSubscriptionBlock-img'
                      >
                        <Image src={Images.pdpSubscriptionBlock.src} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
          </Col>
        </Row>

        {!productInfo.subscriptionPlan ? (
          <>
            <Row className='mt-5'>
              <Col lg={12}>
                <h2>Product Reviews</h2>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <Reviews productInfo={productInfo} />
              </Col>
            </Row>
          </>
        ) : (
          <SubComparisonTable />
        )}
        <SchemaData productInfo={productInfo} />
      </Container>
    );
  }
}

export default Product;
